import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../app.settings';

import { isEmpty } from '../../../common/functions';
import { successMsg } from '../../../common/messages';
import { aggregateValidation } from '../../../common/validation';
import { notifyAndTrackError } from '../../../common/error';

import { AuditInfoComponent, CanComponentDeactivate, ProcessingComponent, UnsavedChangesDialogComponent } from '../../../components';

import { AppInsightsService } from '../../../services/app-insights.service';
import { ContainerAdminService, ContainerTypeAdminService, IContainer, IContainerType } from '../../../services/insecta';
import { MSGraphService } from '../../../services/msgraph.service';

@Component({
  selector: 'app-container-admin',
  standalone: true,
  imports: [
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CommonModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    TableModule,
    ToggleSwitchModule,
    SelectModule,
    AuditInfoComponent,
    ProcessingComponent,
    UnsavedChangesDialogComponent
  ],

  templateUrl: 'container-admin.component.html'
})
export class ContainerAdminComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  containers: IContainer[] = [];
  containerTypes: IContainerType[] = [];
  modifiedContainers: { [s: string]: IContainer } = {};

  dialogConfirmSub: ((leave: boolean) => void) | null = null;

  dialogVars: any = {
    visible: false,
    data: <IContainer>{}
  };

  pageVars: any = {
    currentUser: '',
    isEditing: false,
    isReady: false,
    showUnsavedChangesDialog: false,
    pageId: 'Admin:Containers',
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Container Management' }]
  };

  constructor(
    private apiService: ContainerAdminService,
    private containerTypeService: ContainerTypeAdminService,
    private appInsights: AppInsightsService,
    private graphService: MSGraphService,
    private msgService: MessageService,
    private router: Router,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:Containers`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/containers`);
    this.dialogVars.data = <IContainer>{};
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.pageVars.showUnsavedChangesDialog = true;

      const confirmSub = (decision: boolean) => {
        this.dialogConfirmSub = null;
        resolve(decision);
      };

      this.dialogConfirmSub = confirmSub;
    });
  }

  onDialogConfirm(leave: boolean) {
    this.pageVars.showUnsavedChangesDialog = false;
    if (this.dialogConfirmSub) {
      this.dialogConfirmSub(leave);
    }
  }

  ngOnDestroy() {
    this.containers = [];
    this.containerTypes = [];
    this.pageVars = {
      isReady: false
    };
  }

  ngOnInit() {
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);

    const request = {
      profile: this.graphService.getMyProfile$(),
      containers: this.apiService.getContainers(),
      containerTypes: this.containerTypeService.getContainerTypeAll()
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.containers = response.containers;
        this.containers.sort((a, b) => a.name.localeCompare(b.name));
        this.containerTypes = response.containerTypes;
        this.pageVars.currentUser = response.profile.userPrincipalName;
      })
      .catch((err) => {
        this.appInsights.trackException(err);
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  backClick() {
    this.router.navigate(['admin/globallists']);
  }

  createDlgCancel() {
    this.dialogVars.visible = false;
    this.dialogVars.data = <IContainer>{};
  }

  createDlgSave() {
    this.dialogVars.data.action = `INS`;
    this.dialogVars.data.actionBy = this.pageVars.currentUser;
    this.dialogVars.data.actionOn = new Date(Date.now());
    const containerType = this.containerTypes?.find((a) => a.id === this.dialogVars.data.containerTypeId);
    this.dialogVars.data.containerType = containerType;

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Insert`, { Container: this.dialogVars.data });
    firstValueFrom(this.apiService.addContainer(this.dialogVars.data))
      .then((result) => {
        this.dialogVars.data.id = result;
        this.containers = [this.dialogVars.data, ...this.containers];
        this.containers.sort((a, b) => a.name.localeCompare(b.name));
        successMsg(this.msgService, 'Container has been created');

        this.dialogVars.visible = false;
        this.dialogVars.data = <IContainer>{};
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'Grid:Insert:Error'
        });
      });
  }

  dialogIsValid(): boolean {
    const nameValue = this.dialogVars?.data?.name?.trim() ?? '';
    if (isEmpty(nameValue)) return false;
    if (nameValue === '') return false;

    const containerTypeValue = this.dialogVars?.data?.containerTypeId?.trim() ?? '';
    if (isEmpty(containerTypeValue)) return false;
    if (containerTypeValue === '') return false;

    return true;
  }

  gridNewRow() {
    this.dialogVars.data = <IContainer>{
      isActive: true,
      actionBy: this.pageVars.currentUser
    };

    this.dialogVars.visible = true;
  }

  gridRowEditCancel(c: IContainer, idx: number) {
    this.containers[idx] = this.modifiedContainers[c.id as string];
    delete this.modifiedContainers[c.id as string];
    this.pageVars.isEditing = false;
  }

  gridRowEditInit(c: IContainer) {
    this.pageVars.isEditing = true;
    this.modifiedContainers[c.id as string] = { ...c };
    c.action = 'UPD';
    c.actionBy = this.pageVars.currentUser;
    c.actionOn = new Date(Date.now());
  }

  gridRowEditSave(c: IContainer, idx: number) {
    this.pageVars.isEditing = true;
    this.pageVars.isReady = false;

    const containerType = this.containerTypes.find((a) => a.id === c.containerTypeId);
    c.containerType = <IContainerType>containerType;

    this.appInsights.trackEvent(`${this.pageVars.pageId}:Grid:Save`, { Container: c });

    firstValueFrom(this.apiService.updateContainer(c))
      .then((result) => {
        console.debug(result);
        successMsg(this.msgService, 'Container is updated');
      })
      .catch((err) => {
        this.containers[idx] = this.modifiedContainers[c.id as string];
        if ((<string>err.message).includes('Validation') && err.failures?.length) {
          const rules = err.failures.map((f: any) => ({ valid: false, message: `${f.field}: ${f.message}` }));
          aggregateValidation(this.msgService, rules, { dedupe: true });
        } else {
          notifyAndTrackError(this.msgService, this.appInsights, err, {
            component: this.pageVars.pageId,
            action: 'Grid:Save:Error'
          });
        }
      })
      .finally(() => {
        delete this.modifiedContainers[c.id as string];
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
      });
  }

  toggleStatus(item: IContainer) {
    firstValueFrom(this.apiService.updateContainer(item))
      .then(() => {
        const idx = this.containers.findIndex((x) => x.id == item.id);
        this.containers[idx].isActive = item.isActive;
        this.containers[idx].action = 'UPD';
        this.containers[idx].actionBy = this.pageVars.currentUser;
        this.containers[idx].actionOn = new Date();
        successMsg(this.msgService, 'Container is updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, {
          component: this.pageVars.pageId,
          action: 'ToggleStatus:Error'
        });
      });
  }
}
